import { createContext, useContext, type ReactNode } from "react";

import type { InboxFuryWebPort } from "~/api/ports";

/**
 * The one client every container reads from.
 *
 * Passed by context rather than imported, because the application is run
 * against two different implementations and both have to be first-class: the
 * same-origin BFF in production, and the development-only in-memory client
 * behind `demo.html`, which is what the seeded review flow and the browser
 * end-to-end suite drive. A container that imports a client directly quietly
 * makes one of those impossible.
 */
const ClientContext = createContext<InboxFuryWebPort | undefined>(undefined);

export function ClientProvider({
  client,
  children,
}: {
  client: InboxFuryWebPort;
  children: ReactNode;
}) {
  return <ClientContext.Provider value={client}>{children}</ClientContext.Provider>;
}

export function useClient(): InboxFuryWebPort {
  const client = useContext(ClientContext);
  if (!client) {
    // Failing loudly here beats every container independently rendering an
    // empty state and leaving somebody to wonder why the tenant has no data.
    throw new Error(
      "No InboxFury client in context. Wrap the tree in <ClientProvider client={…}> — " +
        "main.tsx supplies the same-origin BFF client and demo-main.tsx the in-memory one.",
    );
  }
  return client;
}
